
import { useState, useEffect } from "react"

import { useDispatch } from "react-redux"

import { Grid, List, ListItem, ListItemText, Typography, Box, Paper, Chip, Select, MenuItem, FormControl, InputLabel, IconButton, TextField, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button } from "@mui/material"

import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import LinkOffIcon from '@mui/icons-material/LinkOff';

import moment from "moment"

import { InfoField } from "./InfoField";

import { DialogPartesAmparo } from "../dialogs/DialogPartesAmparo";
import { DialogBuscarExpedienteAmparo } from "../dialogs/DialogBuscarExpedienteAmparo";

import { openSnackbar } from "../store/slices/SnackbarSlice"

import { ObtenerTipoAmparo } from "../connections/catalogos/TipoAmparoConnection";
import { ObtenerAutoridadAmparo } from "../connections/catalogos/AutoridadAmparoConnection";
import { AsignarExpedienteAmparo, EditarAmparo } from "../connections/recepcion/AmparoConnection";

import { ParteProps } from "../interfaces/recepcion/ParteInterface";
import { TabValidationProps } from "../interfaces/ui/TabValidation";
import { AmparoProps } from "../interfaces/recepcion/AmparoInterface";
import { AcuerdoProps } from "../interfaces/tribunal/AcuerdoInterface";
import { TipoAmparoProps } from "../interfaces/catalogos/TipoAmparoInterface";
import { defaultExpediente } from "../interfaces/recepcion/ExpedienteInterface";
import { AutoridadAmparoProps } from "../interfaces/catalogos/AutoridadAmparoInterface";

interface Props {
    amparo?:            AmparoProps;
    edit?:              boolean;
    idAcuerdo?:         number;
    setAcuerdo?:        React.Dispatch<React.SetStateAction<AcuerdoProps>>;
    setTabValidation?:  React.Dispatch<React.SetStateAction<TabValidationProps>>;
}

export const BoxInformacionAmparo = ( { amparo, edit = false, idAcuerdo, setAcuerdo, setTabValidation }: Props ) => {

    const dispatch = useDispatch();

    const [openDialogPartes, setOpenDialogPartes] = useState( false );
    const [openDialogExpediente, setOpenDialogExpediente] = useState( false );

    const [openConfirmacionQuitarExpediente, setOpenConfirmacionQuitarExpediente] = useState( false );

    const [tipoAmparo, setTipoAmparo] = useState( 0 );
    const [tipoAmparoArray, setTipoAmparoArray] = useState<TipoAmparoProps[]>( [] );

    const [autoridadAmparo, setAutoridadAmparo] = useState( 0 );
    const [autoridadAmparoArray, setAutoridadAmparoArray] = useState<AutoridadAmparoProps[]>( [] );

    const [numeroAmparoOrigen, setNumeroAmparoOrigen] = useState( amparo?.numeroAmparoOrigen ?? '' );

    const [partes, setPartes] = useState<ParteProps[]>( amparo?.partes ?? [] );

    const [loadingTipoAmparo, setLoadingTipoAmparo] = useState( false );
    const [loadingNumeroAmparoOrigen, setLoadingNumeroAmparoOrigen] = useState( false );
    const [loadingQuitarExpediente, setLoadingQuitarExpediente] = useState( false );

    const handleChangeTipoAmparo = ( value: number ) => {

        setTipoAmparo( value );
        setAutoridadAmparo( value === 1 ? 2 : value === 2 ? 1 : 0 );

    }

    const handleGuardarAmparo = async ( campo: 'tipoAmparo' | 'numeroAmparoOrigen' ) => {

        const setLoading =
            campo === 'tipoAmparo' ? setLoadingTipoAmparo :
                                     setLoadingNumeroAmparoOrigen;

        setLoading( true );

        await EditarAmparo({ idAmparo: amparo?.idAmparo ?? 0, numeroAmparoOrigen: tipoAmparo === 1 ? '' : numeroAmparoOrigen, idTipoAmparo: tipoAmparo, idAutoridadAmparo:  autoridadAmparo }).then( resp => {

            setTimeout(() => {

                const { success, message } = resp;

                if( success ){

                    const tipoAmparoDesc      = tipoAmparoArray.find( (t) => t.idTipoAmparo === tipoAmparo )?.descripcion ?? '';
                    const autoridadAmparoDesc = autoridadAmparoArray.find( (a) => a.idAutoridadAmparo === autoridadAmparo )?.descripcion ?? '';

                    dispatch( openSnackbar({ message, variant: 'success' }) );

                    setAcuerdo?.( prev => ({
                        ...prev,
                        amparo: {
                            ...prev.amparo,
                            numeroAmparoOrigen,
                            idTipoAmparo:       tipoAmparo,
                            tipoAmparo:         tipoAmparoDesc,
                            idAutoridadAmparo:  autoridadAmparo,
                            autoridadAmparo:    autoridadAmparoDesc,
                        }
                    }));

                } else {

                    dispatch( openSnackbar({ message, variant: 'error' }) );

                }

                setLoading( false );

            }, 700);

        });

    }

    const handleQuitarExpediente = async () => {

        setLoadingQuitarExpediente( true );

        await AsignarExpedienteAmparo({ idAmparo: amparo?.idAmparo ?? 0, idExpediente: 0, idAcuerdo }).then( resp => {

            setTimeout(() => {

                const { success, message } = resp;

                if( success ){

                    dispatch( openSnackbar({ message, variant: 'success' }) );
                    setOpenConfirmacionQuitarExpediente( false );

                    setAcuerdo?.( prev => ({
                        ...prev,
                        idExpediente: 0,
                        expediente: defaultExpediente,
                        amparo: {
                            ...prev.amparo,
                            numeroExpediente: '',
                        }
                    }));

                    setTabValidation?.( prev => ({
                        ...prev,
                        idExpediente: 0,
                    }));

                } 
                else {

                    dispatch( openSnackbar({ message, variant: 'error' }) );

                }

                setLoadingQuitarExpediente( false );

            }, 700);

        });

    }

    useEffect(() => {

        async function Obtener() {

            const [tipos, autoridades] = await Promise.all([
                ObtenerTipoAmparo(),
                ObtenerAutoridadAmparo(),
            ]);

            setTipoAmparoArray( tipos );
            setAutoridadAmparoArray( autoridades );

        }

        if( edit ){
            Obtener();
        }

    }, [ edit ]);

    useEffect(() => {

        if( tipoAmparoArray.length !== 0 ){
            setTipoAmparo( amparo?.idTipoAmparo ?? 0 );
        }

    }, [ amparo?.idTipoAmparo, tipoAmparoArray.length ]);

    useEffect(() => {

        const idTipo = amparo?.idTipoAmparo ?? 0;

        if( amparo?.idAutoridadAmparo ){
            setAutoridadAmparo( amparo.idAutoridadAmparo );
        } 
        else {
            setAutoridadAmparo( idTipo === 1 ? 2 : idTipo === 2 ? 1 : 0 );
        }

    }, [ amparo?.idAutoridadAmparo, amparo?.idTipoAmparo, autoridadAmparoArray.length ]);

    useEffect(() => {

        setNumeroAmparoOrigen( amparo?.numeroAmparoOrigen ?? '' );

    }, [ amparo?.numeroAmparoOrigen ]);

    useEffect(() => {

        setPartes( amparo?.partes ?? [] );

    }, [ amparo?.partes ]);

    return (

        <Box p={4} component={ Paper }>

            <Grid container spacing={3}>

                {
                    amparo?.numeroAmparo
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            <InfoField
                                label="Número auxiliar de amparo"
                                value={ amparo?.numeroAmparo }
                                fontSize={ 16 }
                            />

                        </Grid>
                }

                {
                    amparo?.fecha
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            <InfoField
                                label="Fecha"
                                value={ moment( new Date( amparo?.fecha ) ).format( 'DD-MM-YYYY' ) }
                                fontSize={ 16 }
                            />

                        </Grid>
                }
                
                <Grid size={{ md: 6, xs: 12 }}>

                    {
                        edit
                        ?
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

                                <FormControl fullWidth>

                                    <InputLabel shrink>Tipo de amparo</InputLabel>

                                    <Select
                                        notched
                                        label="Tipo de amparo"
                                        value={ tipoAmparo }
                                        onChange={ (e) => handleChangeTipoAmparo( e.target.value as number ) }
                                    >
                                        <MenuItem value={0}>Selecciona una opción</MenuItem>

                                        {
                                            tipoAmparoArray.map( ({ idTipoAmparo, descripcion }) => (
                                                <MenuItem key={ idTipoAmparo } value={ idTipoAmparo }>{ descripcion }</MenuItem>
                                            ))
                                        }

                                    </Select>

                                </FormControl>

                                <IconButton
                                    color='primary'
                                    onClick={ () => handleGuardarAmparo( 'tipoAmparo' ) }
                                    disabled={ loadingTipoAmparo || tipoAmparo === ( amparo?.idTipoAmparo ?? 0 ) }
                                >
                                    { loadingTipoAmparo ? <CircularProgress size={ 20 } /> : <SaveIcon /> }
                                </IconButton>

                            </Box>
                        :
                            amparo?.tipoAmparo
                            &&
                                <InfoField
                                    label="Tipo de amparo"
                                    value={ amparo?.tipoAmparo }
                                    fontSize={ 16 }
                                />
                    }

                </Grid>

                {
                    ( edit || !!amparo?.autoridadAmparo )
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            <InfoField
                                label="Autoridad del amparo"
                                value={ edit
                                    ? ( autoridadAmparoArray.find( (a) => a.idAutoridadAmparo === autoridadAmparo )?.descripcion ?? amparo?.autoridadAmparo ?? '' )
                                    : ( amparo?.autoridadAmparo ?? '' )
                                }
                                fontSize={ 16 }
                            />

                        </Grid>
                }

                {
                    ( edit ? tipoAmparo === 2 : !!amparo?.numeroAmparoOrigen )
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            {
                                edit
                                ?
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

                                        <TextField
                                            fullWidth
                                            label="Número de amparo de origen"
                                            value={ numeroAmparoOrigen }
                                            onChange={ (e) => setNumeroAmparoOrigen( e.target.value ) }
                                            slotProps={{ inputLabel: { shrink: true } }}
                                        />

                                        <IconButton
                                            color='primary'
                                            onClick={ () => handleGuardarAmparo( 'numeroAmparoOrigen' ) }
                                            disabled={ loadingNumeroAmparoOrigen || numeroAmparoOrigen === ( amparo?.numeroAmparoOrigen ?? '' ) }
                                        >
                                            { loadingNumeroAmparoOrigen ? <CircularProgress size={ 20 } /> : <SaveIcon /> }
                                        </IconButton>

                                    </Box>
                                :
                                    <InfoField
                                        label="Número de amparo de origen"
                                        value={ amparo?.numeroAmparoOrigen }
                                        fontSize={ 16 }
                                    />
                            }

                        </Grid>
                }   
                
                {
                    ( !!amparo?.numeroExpediente || edit )
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

                                <InfoField
                                    label="Número de expediente"
                                    value={ amparo?.numeroExpediente ?? '' }
                                    fontSize={ 16 }
                                />

                                {
                                    edit
                                    &&
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>

                                            <IconButton
                                                size='small'
                                                color='primary'
                                                onClick={ () => setOpenDialogExpediente( true ) }
                                            >
                                                <EditIcon fontSize='small' />
                                            </IconButton>

                                            {
                                                !!amparo?.numeroExpediente
                                                &&
                                                    <IconButton
                                                        size='small'
                                                        color='error'
                                                        onClick={ () => setOpenConfirmacionQuitarExpediente( true ) }
                                                    >
                                                        <LinkOffIcon fontSize='small' />
                                                    </IconButton>
                                            }

                                        </Box>
                                }

                            </Box>

                        </Grid>
                }  

                {
                    amparo?.juez
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            <InfoField
                                label="Juez"
                                value={ amparo?.juez }
                                fontSize={ 16 }
                            />

                        </Grid>
                }

                {
                    amparo?.secretario
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            <InfoField
                                label="Secretario(a)"
                                value={ amparo?.secretario }
                                fontSize={ 16 }
                            />

                        </Grid>
                }

                {
                    amparo?.juzgadoOrigen
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            <InfoField
                                label="Juzgado de origen"
                                value={ amparo?.juzgadoOrigen }
                                fontSize={ 16 }
                            />

                        </Grid>
                }

                {
                    ( partes.length !== 0 || edit )
                    &&
                        <Grid size={{ md: 6, xs: 12 }}>

                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>

                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        color: "text.secondary",
                                        letterSpacing: 0.5,
                                        fontWeight: 700,
                                    }}
                                >
                                    Parte(s):
                                </Typography>

                                {
                                    edit
                                    &&
                                        <IconButton
                                            size='small'
                                            color='primary'
                                            onClick={ () => setOpenDialogPartes( true ) }
                                        >
                                            <EditIcon fontSize='small' />
                                        </IconButton>
                                }

                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {
                                    partes.map( ({ persona, tipoParte }, index) => (

                                        <Box
                                            key={ index }
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Typography component='div' variant='h6' fontWeight={'bold'} fontSize={ 16 }>
                                                { persona }
                                            </Typography>

                                            <Chip
                                                label={ tipoParte }
                                                size='small'
                                                sx={{
                                                    ml: 2,
                                                    fontSize: 11,
                                                    borderRadius: '99px',
                                                    backgroundColor: 'action.hover',
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                }}
                                            />

                                        </Box>

                                    ))
                                }
                            </Box>

                        </Grid>
                }

                {
                    amparo?.digitalizacion
                    &&
                        <Grid size={{ md: 12, xs: 12 }}>

                            <List disablePadding>

                                <ListItem sx={{ py: 0, px: 0 }}>

                                    <ListItemText
                                        sx={{ mr: 2 }}
                                        primary={
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    color: "text.secondary",
                                                    letterSpacing: 0.5,
                                                    fontWeight: 700,
                                                }}>
                                                    Digitalización:
                                            </Typography>
                                        }
                                    />

                                </ListItem>

                            </List>

                            <iframe
                                title='iframe-documento'
                                style={{ width: '100%', height: 800, borderRadius: 8 }}
                                src={ amparo?.digitalizacion ?? '' }
                            />

                        </Grid>
                }

            </Grid>

            <DialogPartesAmparo
                idAmparo={ amparo?.idAmparo ?? 0 }
                openDialog={ openDialogPartes }
                handleCloseDialog={ () => setOpenDialogPartes( false ) }
                onGuardar={ ( p ) => {
                    setPartes( p );
                    setAcuerdo?.( prev => ({
                        ...prev,
                        amparo: {
                            ...prev.amparo,
                            partes: p,
                        }
                    }));
                }}
            />

            <DialogBuscarExpedienteAmparo
                idAmparo={ amparo?.idAmparo ?? 0 }
                idAcuerdo={ idAcuerdo }
                openDialog={ openDialogExpediente }
                handleCloseDialog={ () => setOpenDialogExpediente( false ) }
                onGuardar={ ( exp ) => {
                    setAcuerdo?.( prev => ({
                        ...prev,
                        idExpediente:   exp.idExpediente,
                        expediente:     exp,
                        amparo: {
                            ...prev.amparo,
                            numeroExpediente: exp.numeroExpediente,
                        }
                    }));

                    setTabValidation?.( prev => ({
                        ...prev,
                        idExpediente: exp.idExpediente,
                        idAmparo: amparo?.idAmparo ?? 0,
                    }));
                }}
            />

            <Dialog
                fullWidth
                maxWidth='xs'
                open={ openConfirmacionQuitarExpediente }
            >

                <DialogTitle>Confirmación</DialogTitle>

                <DialogContent>

                    <DialogContentText>
                        ¿Desea quitar el expediente asignado al amparo?
                    </DialogContentText>

                </DialogContent>

                <DialogActions>

                    <Button color='secondary' disabled={ loadingQuitarExpediente } onClick={ () => setOpenConfirmacionQuitarExpediente( false ) }>Cancelar</Button>
                    <Button
                        variant='contained'
                        loading={ loadingQuitarExpediente }
                        onClick={ handleQuitarExpediente }
                    >
                        Aceptar
                    </Button>

                </DialogActions>

            </Dialog>

        </Box>
    )
}
